/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { TippyProps } from '@tippyjs/react'
import { EditorContent, EditorEvents, useEditor } from '@tiptap/react'
import cn from 'classnames'
import React, { FC, HTMLProps, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useToggle, useUpdateEffect } from 'react-use'

import styles from './RichTextEditor.module.scss'
import Toolbar from './Toolbar'
import {
  TextEditorExtensionsOptions,
  addEditorEventListener,
  getEditorContentExtensions,
  prepareContent,
  useEditorMenuExtensions,
} from './utils'

import LinkEditor from '~common/RichTextEditor/LinkEditor'
import LinkMenu from '~common/RichTextEditor/LinkMenu'
import Spinner from '~elements/Spinner'
import { pasteRegex } from '~lib/editor/extensions/link'
import { uploadToS3 } from '~lib/s3'
import Text from '~lib/ui-kit/Text'

export type RichTextEditorProps = {
  error?: string
  initialContent?: string
  viewMode?: boolean
  googleDocsEnabled?: boolean // TODO we should create embed node
  confluenceEnabled?: boolean // TODO or use link extension(if we really need to copy content)
  tableEnabled?: boolean
  mediaEnabled?: boolean
  aiAssistEnabled?: boolean
  autoFocus?: boolean
  placeholder?: string
  onCreate?: (props: EditorEvents['create']) => void
  onDestroy?: (props: EditorEvents['destroy']) => void // TODO we can save editor instance and set/get content directly instead of update state after each transaction using onChange cb and replace editor content using useEffect hook
  returnType?: 'json' | 'html' // TODO remove this
  readOnly?: boolean
  onChange?: (content: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onPasteContent?: (content: string) => void
  popupOptions?: Partial<TippyProps>
} & TextEditorExtensionsOptions &
  Omit<HTMLProps<HTMLDivElement>, 'onChange'>

const RichTextEditor: FC<RichTextEditorProps> = ({
  id,
  error,
  initialContent,
  onChange,
  viewMode = false,
  mediaEnabled,
  tableEnabled,
  aiAssistEnabled = true,
  historyDisabled,
  uploadFile = uploadToS3,
  extraExtensions,
  googleDocsEnabled = false,
  confluenceEnabled = false,
  placeholder,
  readOnly,
  returnType = 'json',
  autoFocus,
  className,
  disabled,
  onFocus,
  onBlur,
  onPasteContent,
  onCreate,
  onDestroy,
  ...props
}) => {
  const [loading, toggleLoading] = useToggle(false)
  const menuExtensions = useEditorMenuExtensions({ mediaEnabled, tableEnabled, aiAssistEnabled, toggleLoading })

  const extensions = useMemo(
    () => [
      ...getEditorContentExtensions({
        mediaEnabled,
        tableEnabled,
        historyDisabled,
        uploadFile,
        placeholder,
        extraExtensions,
      }),
      ...menuExtensions,
    ],
    [extraExtensions, historyDisabled, mediaEnabled, menuExtensions, placeholder, tableEnabled, uploadFile]
  )

  const editor = useEditor({
    editable: !viewMode && !disabled && !readOnly,
    content: initialContent ? prepareContent(initialContent) : initialContent,
    extensions,
    onCreate: (editor) => onCreate?.(editor),
    onDestroy: () => onDestroy?.(),
  })

  const [linkMenuOpened, setLinkMenuOpened] = useState(false)
  const [linkEditorOpened, setLinkEditorOpened] = useState(false)
  // const { importFromConfluence } = useImportFromConfluence()

  const closeLinkMenu = useCallback(() => {
    setLinkMenuOpened(false)
  }, [])

  const handleEditorContextMenu = useCallback((e: React.MouseEvent) => {
    if (e.target instanceof Element && e.target.tagName === 'A') {
      e.preventDefault()
      setLinkMenuOpened(true)
    }
  }, [])

  const handleOpenLinkEditor = useCallback(() => {
    setLinkEditorOpened(true)
  }, [])

  const handleCloseLinkEditor = useCallback(() => {
    setLinkEditorOpened(false)
  }, [])

  useEffect(() => {
    return addEditorEventListener(editor, 'transaction', ({ editor, transaction }) => {
      // ignore transaction if content of editor hasn't been changed
      if (!transaction.docChanged) {
        return
      }

      const isHtml = returnType === 'html'
      const isPaste = transaction.getMeta('paste')
      const json = editor.getJSON()
      let shouldOpenLinkMenu = false
      let data

      // eslint-disable-next-line no-prototype-builtins
      if (Array.isArray(json.content) && json.content.length === 1 && !json.content[0].hasOwnProperty('content')) {
        data = ''
      } else if (isHtml) {
        data = editor.getHTML()
      } else {
        data = JSON.stringify(json)
      }

      if (isPaste) {
        const { nodeBefore } = transaction.selection.$to

        if (nodeBefore?.textContent.match(pasteRegex)) {
          shouldOpenLinkMenu = true
        }

        onPasteContent?.(data)
      }

      setLinkMenuOpened(shouldOpenLinkMenu)
      onChange?.(data)
    })
  }, [editor, googleDocsEnabled, onChange, returnType, confluenceEnabled, onPasteContent])

  useUpdateEffect(() => {
    editor?.setEditable(!viewMode && !disabled && !readOnly)
  }, [viewMode, disabled, readOnly])

  const rootClassName = cn(styles.root, { [styles.viewMode]: viewMode, [styles.loading]: loading }, className)

  return (
    <div className={rootClassName} {...props}>
      <EditorContent
        autoFocus={autoFocus}
        editor={editor}
        className={cn(styles.content, disabled && styles.disabled)}
        onContextMenu={handleEditorContextMenu}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {editor && (
        <Toolbar
          opened={!linkEditorOpened}
          editor={editor}
          onCreateLink={handleOpenLinkEditor}
          mediaEnabled={mediaEnabled}
          aiAssistEnabled={aiAssistEnabled}
          tableEnabled={tableEnabled}
          toggleLoading={toggleLoading}
        />
      )}
      {editor && (
        <LinkMenu
          editor={editor}
          googleDocsEnabled={googleDocsEnabled}
          confluenceEnabled={confluenceEnabled}
          opened={linkMenuOpened}
          onShowLinkEditor={handleOpenLinkEditor}
          onClose={closeLinkMenu}
        />
      )}
      {editor && <LinkEditor editor={editor} opened={linkEditorOpened} onClose={handleCloseLinkEditor} />}
      {error && (
        <Text as="div" type="caption" variant="danger" className={styles.error}>
          {error}
        </Text>
      )}
      {/* TODO create plugin */}
      {loading && <Spinner size="m" className={styles.loader} />}
    </div>
  )
}

export default memo(RichTextEditor)
